<template>
  <div class="page-home min-h-screen">
    <section class="mx-auto max-w-10xl px-4 pb-6 pt-10 lg:px-8">
      <div class="mb-4 flex items-center justify-between">
        <RouterLink :to="venuesLink" class="group inline-flex items-center gap-2">
          <h2 class="text-xl font-semibold text-ink transition-colors group-hover:text-brand">
            Popular venues
          </h2>
          <i class="ri-arrow-right-s-line pt-1 text-lg text-ink transition-all group-hover:translate-x-1 group-hover:text-brand" aria-hidden="true"></i>
        </RouterLink>
        <div class="flex items-center gap-2">
          <button class="nav-btn prev prev-venues" aria-label="Previous">
            <i class="ri-arrow-left-s-line"></i>
          </button>
          <button class="nav-btn next next-venues" aria-label="Next">
            <i class="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>

      <div v-if="loading" class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-6 text-sm font-semibold text-muted">
        Loading venues...
      </div>
      <div v-else-if="error" class="rounded-[24px] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
        {{ error }}
      </div>
      <div v-else-if="venueItems.length">
        <Swiper
          class="stay-swiper"
          :modules="[Navigation]"
          :slides-per-view="1.2"
          :space-between="16"
          :navigation="{ prevEl: '.prev-venues', nextEl: '.next-venues' }"
          :breakpoints="{
            640: { slidesPerView: 2.2 },
            768: { slidesPerView: 3.1 },
            1024: { slidesPerView: 4.1 },
            1280: { slidesPerView: 5.1 },
            1536: { slidesPerView: 5.8 }
          }"
        >
          <SwiperSlide v-for="item in venueItems" :key="item.id">
            <RouterLink
              :to="{ name: 'venue-invite-detail', params: { venueId: item.id }, query: detailQuery }"
              class="block h-full"
            >
              <article class="stay-card h-full">
                <div class="stay-img">
                  <img v-if="item.img" :src="item.img" :alt="item.title">
                  <div v-else class="placeholder-cover">
                    {{ item.title.slice(0, 1) }}
                  </div>
                  <span v-if="item.badge" class="badge">{{ item.badge }}</span>
                  <span v-if="item.distanceLabel" class="distance-chip">{{ item.distanceLabel }}</span>
                </div>
                <div class="stay-meta">
                  <h3 class="title">{{ item.title }}</h3>
                  <p class="price">{{ item.subtitle }}</p>
                  <p class="rating">
                    <i class="ri-map-pin-line text-brand"></i>
                    <span>{{ item.location }}</span>
                  </p>
                </div>
              </article>
            </RouterLink>
          </SwiperSlide>
        </Swiper>
      </div>
      <div v-else class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-6 text-sm text-muted">
        No venues found.
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'
import api from '@/services/user/api'
import { useSearchStore } from '@/stores/search'
import 'swiper/css'
import 'swiper/css/navigation'

const searchStore = useSearchStore()

const venues = ref([])
const loading = ref(true)
const error = ref('')
const locationRequested = ref(false)

const latitude = computed(() => String(searchStore.lat || '').trim())
const longitude = computed(() => String(searchStore.lng || '').trim())
const locationLabel = computed(() => String(searchStore.locationText || '').trim())

const detailQuery = computed(() => ({
  location: locationLabel.value || undefined,
  latitude: latitude.value || undefined,
  longitude: longitude.value || undefined
}))

const venuesLink = computed(() => ({
  name: 'venues',
  query: detailQuery.value
}))

const normalizeVenue = (item = {}, index = 0) => ({
  id: String(item?.id || item?.venue_id || `venue-${index}`),
  title: String(item?.title || item?.name || 'Untitled venue').trim(),
  img: String(item?.cover_image || item?.image || '').trim(),
  subtitle: String(item?.tag_line || item?.tagLine || 'Venue details available on inquiry.').trim(),
  location: String(item?.address || 'Location unavailable').trim(),
  distanceLabel: item?.distance ? `${Number(item.distance).toFixed(1)} km away` : '',
  badge: String(item?.type_name || item?.type || '').trim()
})

const venueItems = computed(() => venues.value.map(normalizeVenue))

const loadVenues = async () => {
  loading.value = true
  error.value = ''
  try {
    const formData = new FormData()
    formData.append('id', '')
    formData.append('latitude', latitude.value || '')
    formData.append('longitude', longitude.value || '')
    formData.append('event_type', '')
    const { data } = await api.getVenuesInvite(formData)
    const items = Array.isArray(data?.data) ? data.data : Array.isArray(data?.list) ? data.list : []
    venues.value = items
  } catch (err) {
    error.value = err?.message || 'Failed to load venues.'
    venues.value = []
  } finally {
    loading.value = false
  }
}

const ensureLocation = async () => {
  if (locationRequested.value) return
  if (latitude.value && longitude.value) return
  locationRequested.value = true
  if (typeof navigator === 'undefined' || !navigator.geolocation) return
  await new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        searchStore.setLocation({
          text: locationLabel.value || 'Current location',
          lat: position.coords.latitude,
          lng: position.coords.longitude
        })
        resolve()
      },
      () => resolve(),
      { enableHighAccuracy: true, timeout: 8000 }
    )
  })
}

onMounted(async () => {
  await ensureLocation()
  await loadVenues()
})
</script>

<style scoped>
.stay-card {
  background: #fff;
  border: 1px solid #eaecef;
  border-radius: 22px;
  overflow: hidden;
  transition: transform 0.18s ease, border-color 0.18s ease;
  min-height: 270px;
}

.stay-card:hover {
  transform: translateY(-3px);
  border-color: #d5d9e2;
}

.stay-img {
  position: relative;
  height: 180px;
  overflow: hidden;
  background: linear-gradient(135deg, #f5e8da 0%, #f9f1e8 54%, #efe1d1 100%);
}

.stay-img img,
.placeholder-cover {
  width: 100%;
  height: 100%;
}

.stay-img img {
  object-fit: cover;
}

.placeholder-cover {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.4rem;
  font-weight: 700;
  color: #c69225;
}

.badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 6px 10px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.distance-chip {
  position: absolute;
  right: 12px;
  top: 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.96);
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  color: #6b4d39;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

.stay-meta {
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stay-meta .title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.stay-meta .price {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}

.stay-meta .rating {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
  color: #0f172a;
}

.nav-btn {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: #f2f3f5;
  color: #0f172a;
  transition: transform 0.15s ease, background 0.15s ease;
}

.nav-btn:hover {
  transform: translateY(-1px);
  background: #e5e7eb;
}

.stay-swiper :global(.swiper-slide) {
  height: auto;
}
</style>
